import type { AdfNode } from '@atlassian/adf-node-hasher';

import type { Suggestion } from '../../aiSuggestionsPluginType';

import { deriveHost } from './streaming';

/**
 * `external_config_reference` of the OOTB Content Reviewer agent that owns the
 * add-suggestions-to-content skill.
 */
export const CONTENT_REVIEWER_AGENT_KEY = 'content_reviewer_agent';

/**
 * Provisions the OOTB Content Reviewer agent for the site given by `cloudId` (sent as
 * `Atl-CloudId`). Provisioning is idempotent, so this can be called before every agent
 * lookup — the agent only has to exist before it can be resolved and sent to.
 */
export async function provisionOotbAgent({
	cloudId,
	signal,
}: {
	cloudId: string;
	signal?: AbortSignal;
}): Promise<void> {
	const url = `${deriveHost()}/gateway/api/assist/v1/agent/provisioning/ootb/${CONTENT_REVIEWER_AGENT_KEY}`;
	const response = await fetch(url, {
		method: 'POST',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Product': 'confluence',
			'X-Experience-Id': 'ai-mate',
			'Atl-CloudId': cloudId,
		},
		signal,
	});

	if (!response.ok) {
		throw new Error(`Provision OOTB agent request failed: ${response.status}`);
	}
}

/**
 * Fetches content suggested edits for the given content ARI.
 *
 * @param contentAri - The content ARI to fetch suggested edits for
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Items array from the API response
 */
export async function fetchSuggestions({
	contentAri,
	signal,
}: {
	contentAri: string;
	signal?: AbortSignal;
}): Promise<Suggestion[]> {
	const url = `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/content-suggested-edits/suggestions?content_ari=${encodeURIComponent(
		contentAri,
	)}&status=PENDING`;
	const response = await fetch(url, {
		method: 'GET',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Channel-Id': 'editor',
			'X-Product': 'confluence',
			'X-Experience-Id': 'editor',
		},
		signal,
	});

	if (!response.ok) {
		throw new Error(`Get suggestions request failed: ${response.status}`);
	}

	const data = (await response.json()) as {
		ai_feature_output?: {
			items?: Suggestion[];
		};
	};

	const items = data?.ai_feature_output?.items;
	return Array.isArray(items) ? items : [];
}

export async function updateSuggestionStatus({
	updates,
	signal,
}: {
	signal?: AbortSignal;
	updates: Array<{ id: string; status: 'ACTIONED' | 'DISMISSED' }>;
}): Promise<void> {
	// /ai-features/content-suggested-edits/update-status
	const payload = {
		experience_id: 'ai-content-suggested-edits',
		ai_feature_input: {
			updates,
		},
	};

	const url = `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/content-suggested-edits/update-status`;
	const response = await fetch(url, {
		method: 'POST',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Channel-Id': 'editor',
			'X-Product': 'confluence',
			'X-Experience-Id': 'editor',
		},
		body: JSON.stringify(payload),
		signal,
	});

	if (!response.ok) {
		throw new Error(`Content suggested edits request failed: ${response.status}`);
	}
}

/**
 * Regenerates the given (stale) suggestions against the current node content.
 */
export async function regenerateSuggestions({
	suggestionIds,
	contentAri,
	currentNodes,
	signal,
}: {
	contentAri: string;
	currentNodes: AdfNode[];
	signal?: AbortSignal;
	suggestionIds: string[];
}): Promise<Suggestion[]> {
	const payload = {
		experience_id: 'ai-content-suggested-edits',
		ai_feature_input: {
			content_ari: contentAri,
			current_nodes: currentNodes,
			suggestion_ids: suggestionIds,
		},
	};

	const url = `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/content-suggested-edits/suggestions/regenerate`;
	const response = await fetch(url, {
		method: 'POST',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Channel-Id': 'editor',
			'X-Product': 'confluence',
			'X-Experience-Id': 'editor',
		},
		body: JSON.stringify(payload),
		signal,
	});

	if (!response.ok) {
		throw new Error(`Regenerate suggestions request failed: ${response.status}`);
	}

	const data = (await response.json()) as {
		ai_feature_output?: {
			items?: Suggestion[];
		};
	};

	const items = data?.ai_feature_output?.items;
	return Array.isArray(items) ? items : [];
}
